import { useEffect, useMemo, useState } from 'react';
import { hostListKyc, hostProfile, hostUpdateProfile, hostUploadKyc, hostUploadProfileImage } from '../api/host';
import { useToast } from '../components/ToastContext';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';

const docOptions = [
  { value: 'ID_FRONT', label: 'ID Front' },
  { value: 'ID_BACK', label: 'ID Back' },
  { value: 'PASSPORT', label: 'Passport' },
  { value: 'SELFIE', label: 'Selfie with ID' },
  { value: 'GST', label: 'GST' },
  { value: 'BUSINESS_LICENSE', label: 'Business License' },
];

const fileSizeMb = (file) => (file ? file.size / (1024 * 1024) : 0);
const readableDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const HostProfile = ({ accessToken, onProfileUpdate }) => {
  const toast = useToast();
  const [profile, setProfile] = useState({
    full_name: '',
    business_name: '',
    email: '',
    phone: '',
    address_line1: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
  });
  const [documents, setDocuments] = useState([]);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [kycForm, setKycForm] = useState({ doc_type: 'ID_FRONT', doc_number: '', file: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingKyc, setUploadingKyc] = useState(false);
  const [kycFileKey, setKycFileKey] = useState(0);

  const avatarFallback = useMemo(() => (profile.full_name ? profile.full_name[0]?.toUpperCase() : 'H'), [profile.full_name]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const prof = await hostProfile(accessToken);
        const hostData = prof?.host || prof;
        const resolvedProfile = {
          full_name: hostData?.full_name || '',
          business_name: hostData?.business_name || '',
          email: hostData?.email || '',
          phone: hostData?.phone || '',
          address_line1: hostData?.address_line1 || '',
          city: hostData?.city || '',
          state: hostData?.state || '',
          country: hostData?.country || '',
          pincode: hostData?.pincode || '',
        };
        setProfile(resolvedProfile);
        setProfileImagePreview(resolveAssetUrl(hostData?.profile_image_url || hostData?.photo_url || ''));
        const docs = await hostListKyc(accessToken);
        const mappedDocs = (Array.isArray(docs) ? docs : docs?.documents || []).map((d) => ({
          ...d,
          file_url: resolveAssetUrl(d.file_url),
        }));
        setDocuments(mappedDocs);
      } catch (err) {
        setError(err.message || 'Failed to load profile');
        toast?.error?.(err.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const handleProfileChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await hostUpdateProfile(accessToken, profile);
      const updatedHost = res?.host || res;
      if (updatedHost) {
        setProfile((prev) => ({
          ...prev,
          full_name: updatedHost.full_name || prev.full_name,
          business_name: updatedHost.business_name || prev.business_name,
          email: updatedHost.email || prev.email,
          phone: updatedHost.phone || prev.phone,
          address_line1: updatedHost.address_line1 || prev.address_line1,
          city: updatedHost.city || prev.city,
          state: updatedHost.state || prev.state,
          country: updatedHost.country || prev.country,
          pincode: updatedHost.pincode || prev.pincode,
        }));
      }
      toast?.success?.('Profile updated');
      onProfileUpdate?.(updatedHost || res);
    } catch (err) {
      toast?.error?.(err.message || 'Update failed');
    } finally {
      setSavingProfile(false);
    }
  };

  const uploadAvatar = async () => {
    if (!profileImageFile) {
      toast?.error?.('Choose an image first');
      return;
    }
    if (fileSizeMb(profileImageFile) > 2) {
      toast?.error?.('Max size 2MB');
      return;
    }
    setUploadingImage(true);
    try {
      const res = await hostUploadProfileImage(accessToken, profileImageFile);
      const updatedHost = res?.host || res;
      const newUrl = resolveAssetUrl(updatedHost?.profile_image_url || res?.profile_image_url);
      if (newUrl) {
        setProfileImagePreview(newUrl);
        onProfileUpdate?.(updatedHost || res);
      }
      toast?.success?.('Profile image uploaded');
      setProfileImageFile(null);
    } catch (err) {
      toast?.error?.(err.message || 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const uploadKyc = async (e) => {
    e.preventDefault();
    if (!kycForm.doc_type || !kycForm.doc_number || !kycForm.file) {
      toast?.error?.('Document type, number, and file are required');
      return;
    }
    if (fileSizeMb(kycForm.file) > 5) {
      toast?.error?.('Max size 5MB');
      return;
    }
    setUploadingKyc(true);
    try {
      const res = await hostUploadKyc(accessToken, kycForm);
      const docs = res?.documents || res;
      setDocuments(Array.isArray(docs) ? docs : []);
      toast?.success?.('KYC document uploaded');
      setKycForm({ doc_type: 'ID_FRONT', doc_number: '', file: null });
      setKycFileKey((k) => k + 1);
    } catch (err) {
      toast?.error?.(err.message || 'Upload failed');
    } finally {
      setUploadingKyc(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 text-slate-600">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">Homevilas</div>
        <div className="text-2xl font-bold text-slate-900">Host Profile</div>
        <p className="mt-2 text-sm text-slate-600">Loading your profile...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-8">
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Homevilas</p>
        <h1 className="text-3xl font-bold text-slate-900">Host Profile</h1>
        <p className="text-sm text-slate-600">Manage your profile details and verify your identity.</p>
      </div>

      {error && <div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-900">Profile image</p>
          <p className="text-xs text-slate-500">PNG/JPG/WEBP up to 2MB</p>
          <div className="mt-4 flex items-center gap-4">
            {profileImagePreview ? (
                  <img src={profileImagePreview} alt="Profile" className="h-16 w-16 rounded-full border border-slate-200 object-cover" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-lg font-semibold text-slate-700">
                {avatarFallback}
              </div>
            )}
            <div className="flex flex-col gap-2">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  setProfileImageFile(file || null);
                  if (file) setProfileImagePreview(URL.createObjectURL(file));
                }}
                className="text-sm text-slate-700"
              />
              <button
                type="button"
                onClick={uploadAvatar}
                disabled={uploadingImage}
                className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {uploadingImage ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={saveProfile} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Host Profile</p>
              <p className="text-xs text-slate-500">Keep your contact and business info up to date.</p>
            </div>
            <button
              type="submit"
              disabled={savingProfile}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
            >
              {savingProfile ? 'Saving...' : 'Save changes'}
            </button>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">Full name</label>
              <input
                required
                value={profile.full_name}
                onChange={(e) => handleProfileChange('full_name', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                placeholder="Jane Doe"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">Business name</label>
              <input
                value={profile.business_name}
                onChange={(e) => handleProfileChange('business_name', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                placeholder="Homevilas Pvt Ltd"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">Email</label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => handleProfileChange('email', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                placeholder="you@example.com"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">Phone</label>
              <input
                value={profile.phone}
                onChange={(e) => handleProfileChange('phone', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                placeholder="+91 99999 99999"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-xs font-semibold text-slate-600">Address</label>
              <input
                value={profile.address_line1}
                onChange={(e) => handleProfileChange('address_line1', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                placeholder="Street, area"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">City</label>
              <input
                value={profile.city}
                onChange={(e) => handleProfileChange('city', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">State</label>
              <input
                value={profile.state}
                onChange={(e) => handleProfileChange('state', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">Country</label>
              <input
                value={profile.country}
                onChange={(e) => handleProfileChange('country', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                placeholder="India"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">Pincode</label>
              <input
                value={profile.pincode}
                onChange={(e) => handleProfileChange('pincode', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>
          </div>
        </form>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Upload KYC Document</p>
            <p className="text-xs text-slate-500">JPEG/PNG/PDF up to 5MB</p>
          </div>
        </div>

        <form onSubmit={uploadKyc} className="mt-4 grid gap-3 md:grid-cols-3">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600">Document type</label>
            <select
              value={kycForm.doc_type}
              onChange={(e) => setKycForm((prev) => ({ ...prev, doc_type: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            >
              {docOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600">Document number</label>
            <input
              value={kycForm.doc_number}
              onChange={(e) => setKycForm((prev) => ({ ...prev, doc_number: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              placeholder="Enter document number"
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600">Upload file</label>
            <input
              key={kycFileKey}
              type="file"
              accept="image/png,image/jpeg,application/pdf"
              onChange={(e) => setKycForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div className="md:col-span-3">
            <button
              type="submit"
              disabled={uploadingKyc}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {uploadingKyc ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>

        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Document type</th>
                <th className="px-3 py-2">Number</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Uploaded</th>
                <th className="px-3 py-2">File</th>
                <th className="px-3 py-2">Admin comment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {documents.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-3 text-sm text-slate-500">
                    No documents uploaded yet.
                  </td>
                </tr>
              )}
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-slate-50/70">
                  <td className="px-3 py-2 font-semibold text-slate-900">{doc.doc_type || '—'}</td>
                  <td className="px-3 py-2 text-slate-800">{doc.doc_number || '—'}</td>
                  <td className="px-3 py-2">
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700">
                      {doc.status || 'PENDING'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{readableDate(doc.uploaded_at || doc.created_at)}</td>
                  <td className="px-3 py-2">
                    {doc.file_url ? (
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm font-semibold text-emerald-700 hover:underline"
                      >
                        View file
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{doc.admin_comment || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HostProfile;
